/**
 * Producer data fetching — all registered producers and the active top-21 schedule.
 */

import { rpc } from './rpc.js';
import { TOTAL_BPS } from './constants.js';
import { log } from './logger.js';

/**
 * @typedef {object} Producer
 * @property {string}  name     — EOSIO account name
 * @property {boolean} isActive — Whether regproducer is active
 * @property {number}  rank     — Position in sorted list (1-based)
 * @property {number}  votes    — Total votes (float)
 * @property {string}  url      — BP website URL
 * @property {boolean} isTop21  — Whether in the active top-21 schedule
 */

/**
 * Fetch ALL registered producers from the `eosio::producers` table.
 * Handles pagination automatically.
 *
 * @returns {Promise<Producer[]>} sorted by active-first then votes desc
 */
export async function fetchAllProducers() {
  log('Fetching all registered producers for dropdown...');

  let rows = [];
  let more = true;
  let lowerBound = '';

  while (more) {
    const result = await rpc('/v1/chain/get_table_rows', {
      code: 'eosio',
      table: 'producers',
      scope: 'eosio',
      limit: 200,
      json: true,
      lower_bound: lowerBound,
    });

    if (result.rows.length === 0) break;

    // Avoid duplicate from lower_bound inclusion
    if (lowerBound && result.rows[0]?.owner === lowerBound) {
      result.rows.shift();
    }

    rows = rows.concat(result.rows);
    more = result.more;

    if (more && result.rows.length > 0) {
      lowerBound = result.rows[result.rows.length - 1].owner;
    }
  }

  // Deduplicate
  const seen = new Set();
  rows = rows.filter((r) => {
    if (seen.has(r.owner)) return false;
    seen.add(r.owner);
    return true;
  });

  // Sort: active first (by votes desc), then inactive
  rows.sort((a, b) => {
    if (a.is_active !== b.is_active) return b.is_active - a.is_active;
    return parseFloat(b.total_votes) - parseFloat(a.total_votes);
  });

  // Build Producer objects and tag top 21
  let activeCount = 0;
  const producers = rows.map((r, i) => {
    const isActive = r.is_active === 1;
    if (isActive) activeCount++;
    return {
      name: r.owner,
      isActive,
      rank: i + 1,
      votes: parseFloat(r.total_votes),
      url: r.url || '',
      isTop21: isActive && activeCount <= TOTAL_BPS,
    };
  });

  log(
    `Loaded ${producers.length} registered producers (${producers.filter((p) => p.isTop21).length} in active schedule).`,
    'success'
  );

  return producers;
}

/**
 * Fetch the current active top-21 block producers from the schedule.
 * Falls back to the producers table if the schedule endpoint fails.
 *
 * @param {Producer[]} [cachedProducers=[]] — Previously fetched full producer list (avoids re-fetch)
 * @returns {Promise<string[]>} Array of account names in schedule order
 */
export async function fetchActiveSchedule(cachedProducers = []) {
  log('Fetching active block producer schedule...');

  // Primary: schedule endpoint (canonical ordering)
  try {
    const schedule = await rpc('/v1/chain/get_producer_schedule');
    if (schedule.active?.producers?.length) {
      const names = schedule.active.producers.map((p) => p.producer_name);
      log(`Got ${names.length} BPs from active schedule.`, 'success');
      return names;
    }
  } catch {
    log('Schedule endpoint failed, trying fallback...', 'warn');
  }

  // Fallback 1: use cached list
  if (cachedProducers.length > 0) {
    const names = cachedProducers.filter((p) => p.isTop21).map((p) => p.name);
    if (names.length > 0) {
      log(`Got ${names.length} BPs from cached producer list.`, 'success');
      return names;
    }
  }

  // Fallback 2: fresh table fetch (top 50, sort, take 21)
  const result = await rpc('/v1/chain/get_table_rows', {
    code: 'eosio',
    table: 'producers',
    scope: 'eosio',
    limit: 50,
    json: true,
  });

  const sorted = result.rows
    .filter((r) => r.is_active === 1)
    .sort((a, b) => parseFloat(b.total_votes) - parseFloat(a.total_votes))
    .slice(0, TOTAL_BPS);

  const names = sorted.map((r) => r.owner);
  log(`Got ${names.length} BPs from producers table (sorted by votes).`, 'success');
  return names;
}
